import {z} from 'zod';

export const n8nPositionSchema = z.tuple([z.number(), z.number()]);
export const n8nCredentialsSchema = z.record(
  z.object({
    id: z.string(),
    name: z.string(),
  })
);
export const n8nConnectionTargetSchema = z.object({
  node: z.string(),
  type: z.string(),
  index: z.number(),
});
export type N8nConnectionTarget = z.infer<typeof n8nConnectionTargetSchema>;

/**
 * n8n connections structure schema.
 * Maps source node name -> output type -> array of target groups.
 */
export const n8nConnectionsSchema = z.record(
  z.record(z.array(z.array(n8nConnectionTargetSchema)))
);

export type N8nConnections = z.infer<typeof n8nConnectionsSchema>;

/**
 * n8n node schema.
 * Note: typeVersion can be number or string in real n8n workflows,
 * we use coerce to normalize strings to numbers.
 */
export const n8nNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  typeVersion: z.coerce.number().optional(),
  position: n8nPositionSchema,
  parameters: z.record(z.unknown()).optional(),
  credentials: n8nCredentialsSchema.optional(),
  notes: z.string().optional(),
  webhookId: z.string().optional(),
  disabled: z.boolean().optional(),
});

export type N8nNode = z.infer<typeof n8nNodeSchema>;

export const n8nSettingsSchema = z
  .object({
    saveExecutionProgress: z.boolean().optional(),
    saveManualExecutions: z.boolean().optional(),
    saveDataErrorExecution: z.string().optional(),
    saveDataSuccessExecution: z.string().optional(),
    executionTimeout: z.number().optional(),
    timezone: z.string().optional(),
    executionOrder: z.string().optional(),
    callerPolicy: z.string().optional(),
    availableInMCP: z.boolean().optional(),
  })
  .optional();

export const n8nMetaSchema = z
  .object({
    instanceId: z.string().optional(),
  })
  .nullish();

export const n8nWorkflowSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  nodes: z.array(n8nNodeSchema),
  connections: n8nConnectionsSchema,
  active: z.boolean().optional(),
  settings: n8nSettingsSchema,
  versionId: z.string().optional(),
  meta: n8nMetaSchema,
  tags: z.array(z.unknown()).optional(),
  pinData: z.record(z.unknown()).nullish(),
  // API response fields (optional for parsed files, present in API responses)
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type N8nWorkflow = z.infer<typeof n8nWorkflowSchema>;
